import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';

import { Loading, PageHeader } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import PermissionGate from '../../components/PermissionGate/PermissionGate';

import useComponentState from './../../utils/useComponentState';
import { usePermissions } from '../../hooks/usePermissions';
import { validateEmail } from '../../utils/validations';
import { formatRoleName } from '../../utils/dataFormatter';
import { getAllAdmins, updateUserDetails, deleteUserDetails } from './../../api/user';
import { getRolesList } from '../../api/RolePermission';
import { USER_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';

import styles from './userManagement.module.scss';

const validateEditForm = (editForm) => {
  const errors = {};

  const emailValidation = validateEmail(editForm.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const mobile = editForm.mobileNo ? editForm.mobileNo.trim() : '';
  if (!mobile) {
    errors.mobileNo = "Mobile number can't be empty.";
  } else if (!/^\+94\d{9}$/.test(mobile)) {
    errors.mobileNo = 'Mobile number must start with +94 followed by 9 digits (e.g. +94771234567).';
  }

  if (!editForm.role || editForm.role.trim().length === 0) {
    errors.role = 'Role selection is required.';
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

const UserManagement = (props) => {
  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['Email', 'Role', 'Mobile Number'],
    },
    userData: [],
    roles: [],
    userLoadingError: '',
    loading: false,
    currentPage: 0,
    pageSize: 25,
    filterLoading: false,
    showUserDrawer: false,
    drawerData: null,
    editMode: false,
    editForm: {
      email: '',
      role: '',
      mobileNo: '',
    },
    fieldErrors: {},
    editSaving: false,
    showDeleteConfirmation: false,
    userToDelete: null,
  });

  const { tableHeading, userData, userLoadingError, loading } = state;
  const { can } = usePermissions();

  useEffect(() => {
    getUserDetails();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const roles = await getRolesList(props.auth.auth.token);
      setState({ roles: Array.isArray(roles) ? roles : [] });
    } catch (error) {
      setState({ roles: [] });
    }
  };

  const getUserDetails = async () => {
    setState({ loading: true, userData: [], userLoadingError: '' });
    try {
      const response = await getAllAdmins(props.auth.auth.token, state.currentPage, state.pageSize);
      setState({ userData: response.list || [] });
    } catch (error) {
      setState({
        userLoadingError: 'Oops, something went wrong. Please try again',
      });
    }
    setState({ loading: false });
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;

    setState({
      showUserDrawer: true,
      drawerData: rowData,
      editMode: false,
      editForm: {
        email: rowData.email || '',
        role: rowData.role || (rowData.roles && rowData.roles.join(', ')) || '',
        mobileNo: rowData.mobileNo || '',
      },
    });
  };

  const handleInputChange = (field, value) => {
    setState({
      editForm: { ...state.editForm, [field]: value },
      fieldErrors: { ...state.fieldErrors, [field]: '' },
    });
  };

  const handleSaveUser = async () => {
    if (!can(USER_MANAGEMENT_PERMISSIONS.VIEW_USERS.UPDATE)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }
    const { isValid, errors } = validateEditForm(state.editForm);

    if (!isValid) {
      setState({ fieldErrors: errors });
      return;
    }

    const { editForm, drawerData } = state;
    if (!drawerData) return;

    setState({ editSaving: true });

    const originalEmail = drawerData.email || drawerData.userEmail || drawerData.username || '';
    const enteredEmail = editForm.email ? editForm.email.trim() : '';
    const newEmail = enteredEmail && enteredEmail !== originalEmail ? enteredEmail : null;

    const payload = {
      currentEmail: originalEmail || null,
      newEmail: newEmail,
      mobileNo: editForm.mobileNo ? editForm.mobileNo.trim() : null,
      role: editForm.role ? editForm.role.trim() : (drawerData.role || (drawerData.roles && drawerData.roles.join(', '))) || null,
      merchants: editForm.merchants || drawerData.merchants || null,
      subMerchant: editForm.subMerchant || drawerData.subMerchant || null,
    };

    try {
      console.log('Updating user with payload:', payload);
      await updateUserDetails(props.auth.auth.token, payload);

      const id = drawerData.id || drawerData._id || drawerData.userId || null;

      const updatedList = (state.userData || []).map((u) => {
        const uid = u.id || u._id || u.userId || null;
        const matches = id && uid ? String(uid) === String(id) : (u.email === originalEmail);

        if (matches) {
          return {
            ...u,
            email: payload.newEmail || u.email,
            mobileNo: payload.mobileNo,
            role: payload.role,
            merchants: payload.merchants,
            subMerchant: payload.subMerchant,
          };
        }
        return u;
      });

      setState({
        drawerData: { ...drawerData, ...payload, email: payload.newEmail || originalEmail },
        editMode: false,
        editSaving: false,
        userData: updatedList,
      });

      toast.success('User updated successfully');
    } catch (err) {
      console.error('Update error', err);
      setState({ editSaving: false });
      toast.error('Failed to update user. Please try again.');
    }
  };

  const handleCloseDrawer = () => {
    setState({ showUserDrawer: false, drawerData: null, editMode: false });
  };

  const handleDeleteUserDetail = async (userData) => {
    const currentUserEmail = props.auth.auth?.email || props.auth.auth?.userEmail || props.auth.auth?.username;

    if (currentUserEmail && userData.email && currentUserEmail.toLowerCase() === userData.email.toLowerCase()) {
      toast.error("You can't delete your own user account.");
      return;
    }
    setState({
      showDeleteConfirmation: true,
      userToDelete: userData,
    });
  };

  const handleConfirmDelete = async () => {
    const userData = state.userToDelete;
    if (!userData) return;

    if (!can(USER_MANAGEMENT_PERMISSIONS.VIEW_USERS.DELETE)) {
      toast.error('You are not authorized to perform this action.');
      setState({ showDeleteConfirmation: false, userToDelete: null });
      return;
    }

    try {
      setState({ loading: true, showDeleteConfirmation: false });

      await deleteUserDetails(props.auth.auth.token, userData.id);

      const updatedList = (state.userData || []).filter((u) => {
        const uid = u.id || u._id || u.userId;
        const dataId = userData.id || userData._id || userData.userId;
        return String(uid) !== String(dataId);
      });

      setState({
        userData: updatedList,
        showUserDrawer: false,
        drawerData: null,
        editMode: false,
        loading: false,
        userToDelete: null,
      });

      toast.success('User deleted successfully');
    } catch (err) {
      console.error('Delete error', err);
      setState({ loading: false, userToDelete: null });
      toast.error('Failed to delete user. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setState({ showDeleteConfirmation: false, userToDelete: null });
  }; const renderDrawer = () => {
    const data = state.drawerData;
    if (!data) return null;

    const email = data.email || data.userEmail || data.username || 'N/A';
    const role = data.role || (data.roles && data.roles.join(', ')) || 'N/A';
    const mobileNo = data.mobileNo || 'N/A';

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title >{!state.editMode ? 'User Details' : 'Edit User Details'}</Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            {!state.editMode ? (
              <>
                <div className={styles.detail_row}>
                  <strong>Email:</strong>
                  <span>{email}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Role:</strong>
                  <span>{formatRoleName(role)}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Mobile Number:</strong>
                  <span>{mobileNo}</span>
                </div>
              </>
            ) : (
              <div className={styles.edit_form}>
                <label className={styles.form_label}>
                  Email
                  <input
                    type="text"
                    value={state.editForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email"
                    autoFocus
                    className={state.fieldErrors?.email ? styles.input_error : ''}
                  />
                  {state.fieldErrors?.email && (
                    <span className={styles.error_message}>{state.fieldErrors.email}</span>
                  )}
                </label>

                <label className={styles.form_label}>
                  Role
                  <select
                    value={state.editForm.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className={`form-select ${state.fieldErrors?.role ? styles.input_error : ''}`}
                  >
                    <option value={state.editForm.role}>
                      {formatRoleName(state.editForm.role)}
                    </option>
                    {state.roles
                      .filter((r) => r.name !== state.editForm.role)
                      .map((r) => (
                        <option key={r.name} value={r.name}>
                          {formatRoleName(r.name)}
                        </option>
                      ))}
                  </select>
                  {state.fieldErrors?.role && (
                    <span className={styles.error_message}>{state.fieldErrors.role}</span>
                  )}
                </label>


                <label className={styles.form_label}>
                  Mobile Number
                  <input
                    type="text"
                    value={state.editForm.mobileNo}
                    onChange={(e) =>
                      handleInputChange(
                        'mobileNo',
                        e.target.value.replace(/[^\d+]/g, '').slice(0, 12)
                      )
                    }
                    placeholder="+94771234567"
                    maxLength={12}
                    className={state.fieldErrors?.mobileNo ? styles.input_error : ''}
                  />
                  {state.fieldErrors?.mobileNo && (
                    <span className={styles.error_message}>{state.fieldErrors.mobileNo}</span>
                  )}
                </label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', margin: '0 20px', gap: '10px' }}>
            {!state.editMode ? (
              <>
                <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_USERS.UPDATE}>
                  <button
                    type="button"
                    className={`btn btn-primary ${styles.edit_button}`}
                    onClick={() => setState({ editMode: true })}
                  >
                    Edit
                  </button>
                </PermissionGate>

                <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_USERS.DELETE}>
                  <button
                    type="button"
                    className={`btn btn-danger ${styles.delete_button}`}
                    onClick={() => handleDeleteUserDetail(state.drawerData)}
                  >
                    Delete
                  </button>
                </PermissionGate>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`btn btn-primary ${styles.edit_button}`}
                  onClick={() => setState({ editMode: false })}
                  disabled={state.editSaving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className={`btn btn-primary ${styles.edit_button}`}
                  onClick={handleSaveUser}
                  disabled={state.editSaving}
                >
                  {state.editSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </Drawer.Body>
      </>
    );
  };

  return (
    <div className={styles.root_div}>
      {loading ? (
        <Loading text="Loading" />
      ) : (
        <div>
          <PageHeader text="User Details" />
          {userLoadingError ? (
            <p>{userLoadingError}</p>
          ) : (
            <div>
              <div className={styles.filter_btn_group}></div>

              <DataTable
                tableData={userData}
                tableheading={tableHeading}
                handleOnClick={handleDrawerOpen}
                tag="userDetails"
                fetchError=""
                searchFilterLoading={false}
                handleDeleteUserDetail={handleDeleteUserDetail}
              />
            </div>
          )}
        </div>
      )}

      <CancelAlertPopup
        show={state.showDeleteConfirmation}
        alertMessage={`Are you sure you want to delete the user with email: ${state.userToDelete?.email}? This action cannot be undone.`}
        handleYesBtnClick={handleConfirmDelete}
        handleNoBtnClick={handleCancelDelete}
      />

      <Drawer
        show={state.showUserDrawer}
        onHide={handleCloseDrawer}
        placement="right"
      >
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};
export default connect(mapStateToProps)(UserManagement);